"use client";
import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import DynamicIcon from "../DynamicIcon";
import { useLightbox } from "../../contexts/lightboxContext";

const CustomCursor = () => {
  const size = 50;
  const sizeMain = 10;
  const circle = useRef();
  const mainCircle = useRef();
  const arrowIcon = useRef();
  const mouse = useRef({ x: 0, y: 0 });
  const delayedMouse = useRef({ x: 0, y: 0 });

  const { isOpen } = useLightbox();

  const [isHovered, setIsHovered] = useState(false);
  const [hoverType, setHoverType] = useState(null);

  const selectors = [
    ".custom-cursor-trigger",
    ".arrow-trigger",
    'input[type="text"]',
    'input[type="email"]',
    'input[type="number"]',
    'input[type="submit"]',
    'input[type="image"]',
    "label[for]",
    "select",
    "textarea",
    "button",
    ".link",
    "iframe",
  ].join(",");

  const manageMouseMove = (e) => {
    const { clientX, clientY } = e;
    mouse.current = { x: clientX, y: clientY };
  };

  const lerp = (x, y, a) => x * (1 - a) + y * a;

  const moveCircle = (x, y, element) => {
    gsap.set(element, { x, y, xPercent: -50, yPercent: -50 });
  };

  const animate = () => {
    const { x, y } = delayedMouse.current;

    delayedMouse.current = {
      x: lerp(x, mouse.current.x, 0.03),
      y: lerp(y, mouse.current.y, 0.03),
    };

    moveCircle(delayedMouse.current.x, delayedMouse.current.y, circle.current);
    moveCircle(mouse.current.x, mouse.current.y, mainCircle.current);

    window.requestAnimationFrame(animate);
  };

  useEffect(() => {
    animate();
    window.addEventListener("mousemove", manageMouseMove);

    const handleMouseEnter = (e) => {
      setIsHovered(true);
      if (e.target.matches(".arrow-trigger")) {
        console.log("Hovering over .arrow-trigger");

        setHoverType("arrow");
      } else if (
        e.target.matches(
          'input[type="text"], input[type="email"], input[type="number"], input[type="image"], label[for], select, textarea'
        )
      ) {
        setHoverType("input");
      } else if (e.target.matches("iframe")) {
        setHoverType("iframe");
      } else {
        setHoverType(null);
      }
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      setHoverType(null);
    };

    const addEventListeners = () => {
      const elements = document.querySelectorAll(selectors);
      elements.forEach((el) => {
        el.addEventListener("mouseenter", handleMouseEnter);
        el.addEventListener("mouseleave", handleMouseLeave);
      });
    };

    addEventListeners();

    const observer = new MutationObserver(() => {
      addEventListeners();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    document.body.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", manageMouseMove);
      document.body.removeEventListener("mouseleave", handleMouseLeave);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isHovered) {
      if (hoverType === "iframe") {
        gsap.to(circle.current, {
          opacity: 0,
          duration: 0,
        });
        gsap.to(mainCircle.current, {
          opacity: 0,
          duration: 0,
        });
      } else {
        gsap.to(circle.current, {
          border: "2px solid #f8f8f8",
          backgroundColor: "none",
          duration: 0.3,
          opacity: 0,
        });
        gsap.to(mainCircle.current, {
          scale: 4,
          backgroundColor: "#FFF",
          duration: 0.3,
          opacity: 1,
        });
        if (hoverType === "input" || hoverType === "arrow") {
          gsap.to(arrowIcon.current, {
            opacity: 1,
            duration: 0.3,
          });
        } else {
          gsap.to(arrowIcon.current, {
            opacity: 0,
            duration: 0.3,
          });
        }
      }
    } else {
      gsap.to(circle.current, {
        border: "2px solid grey",
        backgroundColor: "none",
        duration: 0.3,
        opacity: 1,
        filter: "none",
      });
      gsap.to(mainCircle.current, {
        scale: 1,
        backgroundColor: "#94CA9E",
        duration: 0.3,
        opacity: 1,
        filter: "none",
      });
      gsap.to(arrowIcon.current, {
        opacity: 0,
        duration: 0.3,
      });
    }
  }, [isHovered, hoverType]);

  useEffect(() => {
    if (!isOpen) {
      setIsHovered(false);
      setHoverType(null);
    }
  }, [isOpen]);

  return (
    <>
      <div
        ref={mainCircle}
        className="z-[9999998] mix-blend-difference fixed top-0 left-0 bg-surfaceColor-brand-secondary rounded-full pointer-events-none"
        style={{ width: sizeMain, height: sizeMain }}
      >
        <div
          ref={arrowIcon}
          className="absolute top-0 left-0 z-[9999999] w-full h-full flex justify-center items-center mix-blend-difference text-[.35rem]"
        >
          {hoverType === "arrow" && (
            <DynamicIcon iconName={"IoReturnUpForward"} />
          )}
          {hoverType === "input" && <DynamicIcon iconName={"TbCursorText"} />}
        </div>
      </div>
      <div
        ref={circle}
        className="z-[9999990] fixed top-0 left-0 bg-none border-grey-400 border-2 rounded-full pointer-events-none"
        style={{ width: size, height: size }}
      ></div>
    </>
  );
};

export default CustomCursor;
